import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, FormControl, InputLabel, Select,
  MenuItem, Grid, Alert, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, IconButton, TableSortLabel, TablePagination,
} from '@mui/material';
import {
  Refresh, DeleteForever, DeleteSweep, Delete,
} from '@mui/icons-material';
import axios from 'axios';

const PAGE_OPTS = [10, 20, 50, 100, -1];

function SourceChip({ source }) {
  const isAuto = source === 'auto';
  return (
    <Chip
      label={isAuto ? 'Auto' : 'Manual'}
      size="small"
      sx={{
        fontSize: '0.65rem', fontWeight: 700, height: 20,
        bgcolor: isAuto ? 'rgba(156,39,176,0.1)' : 'rgba(33,150,243,0.1)',
        color: isAuto ? '#9C27B0' : '#2196F3',
        border: `1px solid ${isAuto ? '#9C27B0' : '#2196F3'}`,
      }}
    />
  );
}

export default function OrdersScreen({ apiUrl }) {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null });
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState({ msg: '', severity: 'success' });
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortBy, setSortBy] = useState('created_at');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filters, setFilters] = useState({
    symbol: '',
    status: 'all',
    tradeMode: 'all',
    orderType: 'all',
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage === -1 ? 1000 : rowsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (filters.symbol) params.symbol = filters.symbol;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.tradeMode !== 'all') params.trade_mode = filters.tradeMode;
      if (filters.orderType !== 'all') params.order_type = filters.orderType;

      const res = await axios.get(`${apiUrl}/orders/`, { headers, params });
      setOrders(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, page, rowsPerPage, sortBy, sortOrder, filters]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Reset page when filters change
  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleDeleteOrders = async (type) => {
    setDeleting(true);
    setFeedback({ msg: '', severity: 'success' });
    try {
      const endpoint = type === 'all' ? `${apiUrl}/orders/all` : `${apiUrl}/orders/paper`;
      const res = await axios.delete(endpoint, { headers });
      setFeedback({ msg: res.data.message, severity: 'success' });
      setDeleteDialog({ open: false, type: null });
      fetchOrders();
    } catch (err) {
      setFeedback({ msg: err.response?.data?.detail || 'Delete failed', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const deleteOrder = async (orderId) => {
    try {
      await axios.delete(`${apiUrl}/orders/${orderId}`, { headers });
      fetchOrders();
    } catch (err) { console.error(err); }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'filled': return 'success';
      case 'placed': return 'info';
      case 'pending': return 'warning';
      case 'cancelled': case 'failed': case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const handleSort = (col) => {
    setSortOrder(sortBy === col && sortOrder === 'asc' ? 'desc' : 'asc');
    setSortBy(col);
    setPage(0);
  };

  // Calculate summary stats from current page (for display)
  const totalPnl = orders.reduce((sum, o) => sum + (o.profit_loss || 0), 0);
  const filledCount = orders.filter(o => o.status === 'filled').length;
  const withPnl = orders.filter(o => o.profit_loss != null);
  const winners = withPnl.filter(o => o.profit_loss > 0).length;
  const winRate = withPnl.length > 0 ? ((winners / withPnl.length) * 100).toFixed(1) + '%' : 'N/A';

  return (
    <Box data-testid="orders-screen">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4">Order History</Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteSweep />}
            onClick={() => setDeleteDialog({ open: true, type: 'paper' })} data-testid="delete-paper-orders-btn">
            Delete Paper
          </Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteForever />}
            onClick={() => setDeleteDialog({ open: true, type: 'all' })} data-testid="delete-all-orders-btn">
            Delete All
          </Button>
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={fetchOrders} disabled={loading}
            data-testid="refresh-orders-btn">
            Refresh
          </Button>
        </Box>
      </Box>

      {feedback.msg && (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback({ msg: '', severity: 'success' })} data-testid="orders-feedback">
          {feedback.msg}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="Search Symbol"
              value={filters.symbol} onChange={(e) => updateFilter('symbol', e.target.value)} data-testid="filter-symbol" />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={filters.status} label="Status" onChange={(e) => updateFilter('status', e.target.value)} data-testid="filter-status">
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="filled">Filled</MenuItem>
                <MenuItem value="placed">Placed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Mode</InputLabel>
              <Select value={filters.tradeMode} label="Mode" onChange={(e) => updateFilter('tradeMode', e.target.value)} data-testid="filter-mode">
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="paper">Paper</MenuItem>
                <MenuItem value="real">Real</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Order Type</InputLabel>
              <Select value={filters.orderType} label="Order Type" onChange={(e) => updateFilter('orderType', e.target.value)} data-testid="filter-order-type">
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="BUY">BUY</MenuItem>
                <MenuItem value="SELL">SELL</MenuItem>
                <MenuItem value="SHORT">SHORT</MenuItem>
                <MenuItem value="EXIT">EXIT</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Total Orders</Typography>
            <Typography variant="h6" data-testid="total-orders-count">{total}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Filled (page)</Typography>
            <Typography variant="h6" color="success.main">{filledCount}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">P&L (page)</Typography>
            <Typography variant="h6" color={totalPnl >= 0 ? 'success.main' : 'error.main'}>{totalPnl.toFixed(2)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Win Rate (page)</Typography>
            <Typography variant="h6">{winRate}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Orders Table */}
      <Paper sx={{ p: 2 }}>
        {orders.length === 0 ? (
          <Alert severity="info">No orders found matching filters.</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { id: 'created_at', label: 'Time' },
                      { id: 'source', label: 'Source' },
                      { id: 'strategy_name', label: 'Strategy' },
                      { id: 'symbol', label: 'Symbol' },
                      { id: 'order_type', label: 'Type' },
                      { id: 'quantity', label: 'Qty' },
                      { id: 'entry_price', label: 'Entry', align: 'right' },
                      { id: 'exit_price', label: 'Exit', align: 'right' },
                      { id: 'status', label: 'Status' },
                      { id: 'trade_mode', label: 'Mode' },
                      { id: 'profit_loss', label: 'P&L', align: 'right' },
                    ].map(col => (
                      <TableCell key={col.id} align={col.align}>
                        <TableSortLabel active={sortBy === col.id} direction={sortBy === col.id ? sortOrder : 'asc'}
                          onClick={() => handleSort(col.id)}>
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="center"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                      <TableCell>
                        <Typography variant="body2">{new Date(order.created_at).toLocaleDateString()}</Typography>
                        <Typography variant="caption" color="text.secondary">{new Date(order.created_at).toLocaleTimeString()}</Typography>
                      </TableCell>
                      <TableCell><SourceChip source={order.source} /></TableCell>
                      <TableCell><Typography variant="caption" color="primary" fontWeight={600}>{order.strategy_name || '-'}</Typography></TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">{order.symbol}</Typography>
                        <Typography variant="caption" color="text.secondary">{order.exchange}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={order.order_type} size="small"
                          color={order.order_type === 'BUY' ? 'success' : order.order_type === 'SELL' ? 'error' : 'default'} />
                      </TableCell>
                      <TableCell>{order.quantity}</TableCell>
                      <TableCell align="right">{order.entry_price?.toFixed(2)}</TableCell>
                      <TableCell align="right">{order.exit_price ? order.exit_price.toFixed(2) : '-'}</TableCell>
                      <TableCell><Chip label={order.status} size="small" color={getStatusColor(order.status)} /></TableCell>
                      <TableCell>
                        <Chip label={order.trade_mode?.toUpperCase()} size="small"
                          color={order.trade_mode === 'paper' ? 'default' : 'primary'} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold"
                          color={order.profit_loss > 0 ? 'success.main' : order.profit_loss < 0 ? 'error.main' : 'text.secondary'}>
                          {order.profit_loss != null ? order.profit_loss.toFixed(2) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" onClick={() => deleteOrder(order.id)}
                          data-testid={`delete-order-${order.id}`}>
                          <Delete sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={PAGE_OPTS.map(v => v === -1 ? { value: -1, label: 'All' } : v)}
              component="div" count={total} rowsPerPage={rowsPerPage} page={page}
              onPageChange={(e, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }} />
          </>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, type: null })}>
        <DialogTitle>
          {deleteDialog.type === 'all' ? 'Delete All Orders' : 'Delete Paper Orders'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {deleteDialog.type === 'all'
              ? 'This will permanently delete ALL your orders and cannot be undone. Are you sure?'
              : 'This will permanently delete all PAPER trading orders and positions. Are you sure?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, type: null })} data-testid="cancel-delete-btn">Cancel</Button>
          <Button variant="contained" color="error"
            onClick={() => handleDeleteOrders(deleteDialog.type)}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteForever />}
            data-testid="confirm-delete-btn">
            {deleting ? 'Deleting...' : 'Confirm Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
